import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, iif, of } from 'rxjs';
import { map, mergeMap, withLatestFrom, catchError } from 'rxjs/operators';

/* notification service */
import { NotificationService } from 'src/app/features/shared/services/notification-service/notification.service';

/* jsonata object class */
import { JSONAtaObject } from 'src/app/features/shared/helper-classes';

/* lodash lib */
import * as lodash from 'lodash';

/* DataStorage interface for ngrx */
import { DataStorage } from 'src/app/features/data-stores/data-storage.interface';

/* navigate action */
import { Navigate } from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* Workflow Management API Service */
import { WorkflowManagementControllerService } from 'src/app/features/shared/sdk/services/workflow-management-controller.service';

/* client-data-store actions */
import * as ClientDataStoreActions from 'src/app/features/data-stores/client-data-store/state/client-data-store.actions';

/* router-data-store actions */
import * as RouterDataStoreActions from 'src/app/features/data-stores/router-data-store/state/router-data-store.actions';

/* tour-data-store actions */
import * as TourDataStoreActions from 'src/app/features/data-stores/tour-data-store/state/tour-data-store.actions';

/* api services */
import { ParticipantControllerService } from 'src/app/features/shared/sdk/services';

export const navigatePathAfterCreatingInstance = '/users/profile';
export const navigatePathAfterUpdatingInstance = '/users/profile';
@Injectable()
export class ClientDataStoreEffects {
  constructor(
    private actions$: Actions,
    private store: Store<DataStorage>,
    private notificationService: NotificationService,
    private workflowManagementApi: WorkflowManagementControllerService,
    private clientApiService: ParticipantControllerService,
  ) {
    // prettier-ignore
    // #region customCode: data-store-effects__constructor__start

    // #endregion customCode: data-store-effects__constructor__start

    // prettier-ignore
    // #region customCode: data-store-effects__constructor__end

    // #endregion customCode: data-store-effects__constructor__end
  }

  GetActiveClientId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.InitFetchingActiveClientWithRelations),
      withLatestFrom(
        this.store.select((state) => state.RouterDataStore.params),
      ),
      /* dataset */
      mergeMap(([action, params]) => {
        const data = {
          eventPayload: action['payload'] ?? {},
          params,
        };
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
        /* Pre Data Transformation Step 1 ( Map ) */
        /* get value of the passed jsonata object */
        const data = <any>payloadAsJsonataObj.value;
        /* transform data */
        const transformedData = JSONAtaObject.chain(data).transform(`
              { "id": params.id }
            `);
        return transformedData;
      }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
        /* get value of the passed jsonata object */
        const data = <any>jsonataObj.value;
        return [
          ClientDataStoreActions.GetActiveClientWithRelationsById({ payload: data }),
        ];
      }),
    ),
  );

  navigateToStep2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.FormStep1Submit),
      withLatestFrom(
        this.store.select((state) => state.TourDataStore.tours),
        this.store.select((state) => state.ClientDataStore.step1ButtonClick),
        this.store.select((state) => state.ClientDataStore.gender),
        this.store.select((state) => state.ClientDataStore.countryOfCitizenship),
        this.store.select((state) => state.ClientDataStore.lastName),
        this.store.select((state) => state.ClientDataStore.dateOfBirth),
        this.store.select((state) => state.RouterDataStore.params),
        this.store.select((state) => state.ClientDataStore.firstName),
        this.store.select((state) => state.ClientDataStore.clients),
      ),
      /* dataset */
      mergeMap(
        ([
          action,
          tours,
          step1ButtonClick,
          gender,
          countryOfCitizenship,
          lastName,
          dateOfBirth,
          params,
          firstName,
          clients,
        ]) => {
          const data = {
            eventPayload: action['payload'] ?? {},
            tours,
            step1ButtonClick,
            gender,
            countryOfCitizenship,
            lastName,
            dateOfBirth,
            params,
            firstName,
            clients,
          };
          const initialPayload = of(JSONAtaObject.chain(data));
          return initialPayload;
        },
      ),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
        /* Pre Data Transformation Step 1 ( Map ) */
        /* get value of the passed jsonata object */
        const data = <any>payloadAsJsonataObj.value;
        /* transform data */
        const transformedData = JSONAtaObject.chain(data).transform(`
              {
            "path": (
              countryOfCitizenship = true and tours[id = $$.clients[id = $$.params.id].tourId].tourItinerary = "Yurt içi" ? ("/users/edit/client-information-step2/" & $.params.id) :
              countryOfCitizenship = true and tours[id = $$.clients[id = $$.params.id].tourId].tourItinerary = "Yurt dışı" ? ("/users/edit/client-information-step-foreign/" & $.params.id) :
              countryOfCitizenship = "notTC" ? ("/users/edit/client-information-step3/" & $.params.id) :
              ""
            )
          }
            `);
        return transformedData;
      }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
        /* get value of the passed jsonata object */
        const data = <any>jsonataObj.value;
        return [
          RouterDataStoreActions.Navigate({ payload: data }),
        ];
      }),
    ),
  );


  navigateToHome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.UpdateUserClient),
      /* dataset */
      mergeMap((action) => {
        const data = action['payload'] ?? {};
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__start

      // #endregion customCode: data-store-effects__pre-data-transformation__start

      /* pre data transformation */
      mergeMap(payloadAsJsonataObj => {
        /* Pre Data Transformation Step 1 ( Map ) */
        /* get value of the passed jsonata object */
        const data = <any>payloadAsJsonataObj.value;
        /* transform data */
        const transformedData = JSONAtaObject.chain(data).transform(`
              
          {
              "path":"users/information-completion"
          }
            `);
        return transformedData;
      }),
      // prettier-ignore
      // #region customCode: data-store-effects__pre-data-transformation__end

      // #endregion customCode: data-store-effects__pre-data-transformation__end
      /* trigger success actions */
      mergeMap((jsonataObj) => {
        /* get value of the passed jsonata object */
        const data = <any>jsonataObj.value;
        return [
          RouterDataStoreActions.Navigate({ payload: data }),
        ];
      }),
    ),
  );

  updateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.UpdateClientTourStatus),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: { status: action.payload.status }
        };
        return this.clientApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Başarılı',
              'Katılımcı durumu başarıyla güncellendi.',
            );
            return [
              ClientDataStoreActions.GetClients(),
              // Navigate({
              //   payload: { path: navigatePathAfterUpdatingInstance },
              // }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Hata',
              'Katılımcı durumu güncellenirken bir hata oluştu.',
            );
            return [ClientDataStoreActions.UpdateClientFailure()];
          }),
        );
      }),
    ),
  );

  getActiveTours$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.GetClientsSuccessful),
      /* dataset */
      mergeMap((action) => {
        const data = action['payload'] ?? {};
        const initialPayload = of(JSONAtaObject.chain(data));
        return initialPayload;
      }),

      /* trigger success actions */
      mergeMap((jsonataObj) => {
        /* get value of the passed jsonata object */
        const data = <any>jsonataObj.value;
        return [
          TourDataStoreActions.InitFetchingActiveTourWithRelations(),
          TourDataStoreActions.GetTours(),
        ];
      }),
    ),
  );

  getClients$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.GetClients),
      withLatestFrom(
        this.store.select((state) => state.AuthDataStore.user),
      ),
      mergeMap(([action, user]) => {
        return this.clientApiService.find().pipe(
          mergeMap((clients) => {
            const actions = [];
            const payload: any = {
              clients: clients,
            };

            if (user?.email) {
              const activeClient: any = clients.find((client: any) => {
                return client.email === user.email;
              });

              if (activeClient) {
                actions.push(ClientDataStoreActions.GetActiveClientByIdWithRelationsSuccessful({
                  payload: { activeClient }
                }));
              }

            }

            actions.push(ClientDataStoreActions.GetClientsSuccessful({ payload }));

            return actions;
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while fetching data.',
            );
            return [ClientDataStoreActions.GetClientsFailure()];
          }),
        );
      }),
    ),
  );

  getClientById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.GetClientById),
      mergeMap((action) => {
        return this.clientApiService
          .findById({
            id: action.payload.id,
          })
          .pipe(
            mergeMap((client) => {
              const payload: any = {
                client: client,
              };
              return [
                ClientDataStoreActions.GetClientByIdSuccessful({ payload }),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [ClientDataStoreActions.GetClientByIdFailure()];
            }),
          );
      }),
    ),
  );

  getActiveClientWithRelationsById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.GetActiveClientWithRelationsById),
      mergeMap((action) => {
        return this.clientApiService
          .findById({
            id: action.payload.id,
            filter: {
              include: ['tour'],
            },
          })
          .pipe(
            mergeMap((client) => {
              const payload: any = {
                activeClient: client,
              };
              return [
                ClientDataStoreActions.GetActiveClientByIdWithRelationsSuccessful(
                  { payload },
                ),
              ];
            }),
            catchError((error) => {
              this.notificationService.createNotification(
                'error',
                'Failure',
                'There was an error while fetching data.',
              );
              return [
                ClientDataStoreActions.GetActiveClientByIdWithRelationsFailure(),
              ];
            }),
          );
      }),
    ),
  );

  createClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.CreateClient),
      mergeMap((action) => {
        const params = {
          body: lodash.cloneDeep(action.payload.client),
        };
        /* add defaults */
        params.body.kvkkConfirmation = params.body.kvkkConfirmation ?? false;
        return this.clientApiService.create(<any>params).pipe(
          mergeMap((client) => {
            // this.notificationService.createNotification(
            //   'success',
            //   'Successful',
            //   'Your data has been saved successfully.',
            // );
            const actions: Action[] = [];
            const payload: any = {
              client: client,
            };
            let tourDetailsPagePath = '';
            if (client.tourId) {
              tourDetailsPagePath = '/tours/detail/' + client.tourId;
              actions.push(ClientDataStoreActions.CreateClientSuccessful({ payload }),
                Navigate({
                  payload: { path: tourDetailsPagePath ?? navigatePathAfterCreatingInstance },
                })
              )
            }
            return actions;
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while saving the data.',
            );
            return [ClientDataStoreActions.CreateClientFailure()];
          }),
        );
      }),
    ),
  );

  updateClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.UpdateClient),
      mergeMap((action) => {
        const params = {
          id: action.payload.id,
          body: { ...action.payload.data },
        };

        if (params.body.createdDate) {
          delete params.body.createdDate;
        }
        return this.clientApiService.updateById(<any>params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Veri güncellendi',
              'Verileriniz başarıyla güncellendi.',
            );
            return [
              ClientDataStoreActions.GetClients(),
              Navigate({
                payload: { path: navigatePathAfterUpdatingInstance },
              }),
            ];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while updating the data.',
            );
            return [ClientDataStoreActions.UpdateClientFailure()];
          }),
        );
      }),
    ),
  );

  deleteClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientDataStoreActions.DeleteClient),
      mergeMap((action) => {
        const params = { id: action.payload.id };
        return this.clientApiService.deleteById(params).pipe(
          mergeMap(() => {
            this.notificationService.createNotification(
              'success',
              'Success',
              'Your data has been deleted successfully.',
            );
            const payload: any = {
              id: action.payload.id,
            };
            return [ClientDataStoreActions.DeleteClientSuccessful({ payload })];
          }),
          catchError((error) => {
            this.notificationService.createNotification(
              'error',
              'Failure',
              'There was an error while deleting the data.',
            );
            return [ClientDataStoreActions.DeleteClientFailure()];
          }),
        );
      }),
    ),
  );
}
